import { useState, useEffect } from "react";
import api, { formatApiErrorDetail, BACKEND_URL } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { FileText, X } from "lucide-react";

const resolveUrl = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

/** Center media: public gallery photos + downloadable PDF documents. */
export const CenterMediaPanel = ({ user, onUpdated }) => {
  const [gallery, setGallery] = useState(user.center_gallery || []);
  const [documents, setDocuments] = useState(user.center_documents || []);
  const [docTitle, setDocTitle] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setGallery(data.center_gallery || []);
        setDocuments(data.center_documents || []);
      } catch (_e) {
        /* keep initial props */
      }
    })();
  }, []);

  const saveGallery = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put("/center/media", { gallery: next });
      setGallery(data.center_gallery);
      onUpdated?.({ center_gallery: data.center_gallery });
      toast.success("Gallery updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update gallery");
    } finally {
      setBusy(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (gallery.length >= 12) {
      toast.error("Max 12 gallery photos");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await saveGallery([...gallery, data.url]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      setBusy(false);
    } finally {
      e.target.value = "";
    }
  };

  const handleDocUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!docTitle.trim()) {
      toast.error("Give the document a title first");
      e.target.value = "";
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data: up } = await api.post("/upload/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: doc } = await api.post("/center/documents", { title: docTitle.trim(), url: up.url });
      setDocuments((prev) => [...prev, doc]);
      setDocTitle("");
      toast.success("Document published to your page");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Document upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const removeDoc = async (id) => {
    try {
      await api.delete(`/center/documents/${id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
      toast.success("Document removed");
    } catch (_e) {
      toast.error("Could not remove document");
    }
  };

  return (
    <div data-testid="center-media-panel" className="space-y-10">
      <div className="border border-[#E5E0D8] bg-white p-6">
        <p className="label-eyebrow text-[#1E3A8A]">— Hub photo gallery</p>
        <p className="text-sm text-[#555] mt-2">
          Studio and exhibition photos shown on your public page (max 12).
        </p>
        <div className="mt-4 grid grid-cols-3 md:grid-cols-6 gap-3">
          {gallery.map((url, i) => (
            <div key={url} className="relative aspect-square bg-[#F2EDE5] group" data-testid={`center-gallery-photo-${i}`}>
              <img src={resolveUrl(url)} alt={`${user.name} gallery ${i + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                data-testid={`center-gallery-remove-${i}`}
                onClick={() => saveGallery(gallery.filter((g) => g !== url))}
                className="absolute top-1 right-1 bg-white/90 border border-[#E5E0D8] p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove photo"
              >
                <X className="w-3 h-3 text-[#D32F2F]" />
              </button>
            </div>
          ))}
          {gallery.length < 12 && (
            <label
              data-testid="center-gallery-add"
              className="aspect-square border-2 border-dashed border-[#E5E0D8] hover:border-[#1E3A8A] flex items-center justify-center cursor-pointer text-[#555] hover:text-[#1E3A8A] transition-colors"
            >
              <span className="label-eyebrow text-xs">{busy ? "…" : "+ Add"}</span>
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoUpload} className="hidden" disabled={busy} />
            </label>
          )}
        </div>
      </div>

      <div className="border border-[#E5E0D8] bg-white p-6">
        <p className="label-eyebrow text-[#1E3A8A]">— Documents & catalogues</p>
        <p className="text-sm text-[#555] mt-2">
          PDF brochures and catalogues visitors can download from your public page (max 15MB each).
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-3 items-start">
          <Input
            data-testid="center-doc-title"
            value={docTitle}
            onChange={(e) => setDocTitle(e.target.value)}
            placeholder="Document title, e.g. 2026 Catalogue"
            className="rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111] sm:max-w-sm"
          />
          <label
            data-testid="center-doc-upload"
            className="cursor-pointer rounded-none border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors px-5 py-2.5 label-eyebrow"
          >
            {busy ? "Uploading…" : "Upload PDF"}
            <input type="file" accept="application/pdf" onChange={handleDocUpload} className="hidden" disabled={busy} />
          </label>
        </div>
        {documents.length > 0 && (
          <div className="mt-6 border border-[#E5E0D8]">
            {documents.map((d) => (
              <div key={d.id} data-testid={`center-doc-${d.id}`} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E5E0D8] last:border-0">
                <a href={resolveUrl(d.url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#1E3A8A] hover:underline">
                  <FileText className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm">{d.title}</span>
                </a>
                <Button
                  data-testid={`center-doc-remove-${d.id}`}
                  variant="outline"
                  onClick={() => removeDoc(d.id)}
                  className="rounded-none border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white px-3 py-1 h-auto label-eyebrow text-xs"
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
